import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles, ShieldCheck, Compass, Heart, Award } from "lucide-react";

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const PILLARS = [
  {
    Icon: ShieldCheck,
    title: "Responsible AI",
    body:
      "Every score, insight and recommendation is grounded in your answers, uses non-diagnostic language, and always encourages consulting a real clinician for anything serious.",
  },
  {
    Icon: Compass,
    title: "Prevention over reaction",
    body:
      "Most lifestyle conditions build quietly for years. SilentScan AI turns that silent buildup into a clear, personalized signal — before symptoms show.",
  },
  {
    Icon: Heart,
    title: "Radically accessible",
    body:
      "SilentScan AI is free. No paywalls, no premium tiers. A 15-year-old in a tier-3 town gets the same intelligence as anyone else.",
  },
];

const MANIFESTO = [
  {
    n: "01",
    t: "Health is data — but people are not.",
    b: "Numbers alone don't move behavior. Empathy, context and stories do. The AI must sound human because it's talking to a human.",
  },
  {
    n: "02",
    t: "Awareness before diagnosis.",
    b: "SilentScan AI never labels a person as sick. It highlights signals early enough that a doctor can catch what matters.",
  },
  {
    n: "03",
    t: "Your data lives with you.",
    b: "Nothing you share leaves the session unless you save it. No dark patterns, no ads, no data-brokering.",
  },
  {
    n: "04",
    t: "India first — but not India only.",
    b: "The diet planner speaks in idli, dal, jowar and coconut water because most health apps don't. The world is bigger than California.",
  },
];

export default function Founder() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="min-h-screen bg-[#05050A] text-white">
      {/* Ambient */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          background:
            "radial-gradient(45% 30% at 15% 8%, rgba(0,229,255,0.14) 0%, transparent 60%), radial-gradient(35% 25% at 90% 90%, rgba(11,61,145,0.35) 0%, transparent 60%)",
        }}
      />
      {/* Top bar */}
      <div className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            data-testid="founder-back-home"
            className="inline-flex items-center gap-2 text-zinc-300 hover:text-white text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            SilentScan<span className="text-cyan-400">.AI</span>
          </Link>
          <Link
            to="/dashboard"
            data-testid="founder-to-dashboard"
            className="text-xs text-zinc-400 hover:text-white px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/5"
          >
            Open Dashboard
          </Link>
        </div>
      </div>

      <motion.main
        variants={container}
        initial="hidden"
        animate="show"
        className="max-w-6xl mx-auto px-6"
      >
        {/* Hero */}
        <section className="pt-20 pb-24 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-10 items-end">
          <div>
            <motion.div variants={item} className="text-[10px] font-semibold uppercase tracking-[0.4em] text-cyan-300/80">
              Chapter 00 · The Founder
            </motion.div>
            <motion.h1
              variants={item}
              className="mt-4 font-display text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tight leading-[0.98]"
            >
              A quiet AI for a<br />
              <span className="italic text-cyan-300">loud, distracted</span>
              <br />
              healthcare world.
            </motion.h1>
            <motion.p
              variants={item}
              className="mt-8 text-zinc-300 leading-relaxed max-w-2xl text-lg"
            >
              I&apos;m{" "}
              <span className="text-white font-semibold">Sajiith Vivan</span> —
              a student, a builder, and someone who lost too many people to
              conditions that could have been spotted years earlier. SilentScan
              AI is my attempt to give every person, in every corner of India,
              a preventive-health partner that speaks their language and
              respects their privacy.
            </motion.p>

            <motion.div variants={item} className="mt-8 flex flex-wrap items-center gap-3">
              <span
                data-testid="founder-record-badge"
                className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/40 bg-gradient-to-r from-amber-400/15 to-yellow-500/10 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-200"
              >
                <Award className="w-3.5 h-3.5" />
                India Book of Records Holder
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-300">
                <Sparkles className="w-3.5 h-3.5 text-cyan-300" />
                Level-2 Student Entrepreneur · 2026
              </span>
            </motion.div>

            <motion.div variants={item} className="mt-6 flex items-center gap-3 text-xs text-zinc-500 uppercase tracking-widest">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Currently building
              </span>
              <span className="w-px h-3 bg-white/10" />
              <span>SilentScan AI · NeuroRehab AI</span>
            </motion.div>
          </div>

          {/* Portrait card — monogram plate */}
          <motion.div
            variants={item}
            className="relative aspect-[3/4] rounded-3xl overflow-hidden border border-white/10 bg-gradient-to-br from-[#0B3D91]/40 to-cyan-500/10 flex flex-col justify-end p-6"
          >
            <div
              aria-hidden
              className="absolute inset-0 opacity-40"
              style={{
                background:
                  "radial-gradient(60% 40% at 50% 30%, rgba(0,229,255,0.35), transparent 60%)",
              }}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="font-display text-[220px] leading-none font-semibold text-white/[0.08] tracking-tighter select-none">
                SV
              </div>
            </div>
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-300/80">
                Founder · Engineer · Student
              </div>
              <div className="mt-1 font-display text-2xl font-semibold text-white">
                Sajiith Vivan
              </div>
              <div className="mt-1 text-xs text-zinc-300">
                Chennai · India
              </div>
              <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-amber-300/40 bg-amber-400/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-200">
                <Award className="w-3 h-3" />
                India Book of Records Holder
              </div>
            </div>
          </motion.div>
        </section>

        {/* Editorial marquee */}
        <motion.div
          variants={item}
          className="border-y border-white/5 py-8 overflow-hidden"
        >
          <div className="flex whitespace-nowrap animate-marquee gap-16 text-[42px] sm:text-[64px] font-display font-semibold tracking-tighter">
            <span className="text-white/10">Preventive first.</span>
            <span className="text-cyan-300/40 italic">India-native.</span>
            <span className="text-white/10">Explainable AI.</span>
            <span className="text-cyan-300/40 italic">Free, forever.</span>
            <span className="text-white/10">Preventive first.</span>
            <span className="text-cyan-300/40 italic">India-native.</span>
            <span className="text-white/10">Explainable AI.</span>
            <span className="text-cyan-300/40 italic">Free, forever.</span>
          </div>
        </motion.div>

        {/* Pillars */}
        <section className="pt-24 pb-16">
          <motion.div variants={item} className="max-w-2xl">
            <div className="text-[10px] font-semibold uppercase tracking-[0.4em] text-cyan-300/80">
              What guides us
            </div>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl font-semibold leading-tight">
              Three non-negotiables.
            </h2>
          </motion.div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
            {PILLARS.map((p) => (
              <motion.div
                key={p.title}
                variants={item}
                whileHover={{ y: -4 }}
                className="rounded-3xl border border-white/10 bg-white/[0.03] p-6"
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-400/20 to-blue-500/10 border border-cyan-400/30 flex items-center justify-center">
                  <p.Icon className="w-5 h-5 text-cyan-300" />
                </div>
                <h3 className="mt-4 font-display text-xl font-semibold text-white">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                  {p.body}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Manifesto */}
        <section className="pb-24">
          <motion.div variants={item} className="max-w-2xl">
            <div className="text-[10px] font-semibold uppercase tracking-[0.4em] text-cyan-300/80">
              The Manifesto
            </div>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl font-semibold leading-tight">
              Four beliefs we build with.
            </h2>
          </motion.div>
          <div className="mt-10 flex flex-col">
            {MANIFESTO.map((m, idx) => (
              <motion.div
                key={m.n}
                variants={item}
                className="grid grid-cols-[80px_minmax(0,1fr)] sm:grid-cols-[120px_minmax(0,1fr)_minmax(0,1fr)] gap-4 sm:gap-10 py-8 border-t border-white/5"
              >
                <div className="font-display text-4xl sm:text-5xl font-semibold tabular-nums text-cyan-300/80">
                  {m.n}
                </div>
                <h3 className="font-display text-2xl sm:text-3xl font-semibold leading-tight text-white">
                  {m.t}
                </h3>
                <p className="col-span-2 sm:col-span-1 text-zinc-400 leading-relaxed">
                  {m.b}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <motion.section
          variants={item}
          className="mb-24 rounded-3xl border border-white/10 bg-gradient-to-br from-[#0B3D91]/40 to-cyan-500/10 p-10 sm:p-14 text-center"
        >
          <Sparkles className="w-6 h-6 text-cyan-300 mx-auto" />
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-semibold">
            Know before symptoms show.
          </h2>
          <p className="mt-3 text-zinc-300 max-w-xl mx-auto">
            Run a 16-question preventive check-in and meet your AI Health Twin.
            Free. Private. In 3 minutes.
          </p>
          <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
            <Link
              to="/dashboard"
              data-testid="founder-cta-dashboard"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-black font-medium text-sm hover:bg-cyan-100"
            >
              Open my dashboard
            </Link>
            <Link
              to="/"
              data-testid="founder-cta-home"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-white text-sm hover:bg-white/5"
            >
              Back to home
            </Link>
          </div>
        </motion.section>
      </motion.main>
    </div>
  );
}
